import { useCallback, useMemo, useState } from 'react';

import { YAxis, CartesianGrid, ResponsiveContainer, ComposedChart, ReferenceLine } from 'recharts';

import { DisplaySymbol, DisplayUnit, Nil, Segmentation } from '@core';
import { CorridorParameterMargins, GraphLineNode } from '@data';
import { useTranslate } from '@i18n';

import { colors, graphPalette } from '../../colors';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { typography } from '../../typography';

import { Legend } from './components/LegendAside';
import * as css from './Graph.css';
import { renderXAxis, renderLines, renderCorridors } from './renderers';

export type CorridorDataPoint = CorridorParameterMargins & {
    name: string;
    frame: number;
};

export type MeasurementValue = {
    current: number | Nil;
    comparison: number | Nil;
    displayUnit: DisplayUnit;
    displaySymbol: DisplaySymbol;
};

export type GraphData = {
    dataPoints: Array<Record<string, MeasurementValue | Nil>>;
    isQuickAnalysis: boolean;
    apex: Record<string, { frame: number; value: number }>;
    transition: Record<string, { frame: number; value: number }>;
    displaySymbol: DisplaySymbol;
    corridorDataPoints?: Record<string, CorridorDataPoint[]>;
};

export type GraphProps = {
    data: GraphData;
    lines?: GraphLineNode[];
    segmentation: { current: Segmentation; comparison: Segmentation | null };
    currentFrame?: number;
    activePosition?: number | Nil;
    showComparison: boolean;
    guideParameters?: Record<string, number>;
    onClickGuide?: (smpId: number) => void;
};

export function Graph({
    data: { dataPoints, apex, transition, isQuickAnalysis, displaySymbol, corridorDataPoints },
    lines,
    segmentation,
    currentFrame = 1,
    showComparison,
    guideParameters,
    onClickGuide,
    activePosition = 1,
}: GraphProps) {
    const [filteredDataKeys, setFilteredDataKeys] = useState<string[]>([]);
    const [ref, isVisible] = useIntersectionObserver({ threshold: 0.2 });

    const onClickLegend = useCallback((dataKey: string) => {
        setFilteredDataKeys((prev) =>
            prev.includes(dataKey) ? prev.filter((item) => item !== dataKey) : [...prev, dataKey],
        );
    }, []);

    const colorMap = useMemo(
        () => (lines ? Object.fromEntries(lines?.map((d, i) => [d.id, Object.values(graphPalette)[i]])) : {}),
        [lines],
    );

    const showComparisonData = Object.keys(apex).length === 0 && Object.keys(transition).length === 0;

    const deferExpensiveOperations = !isVisible;

    // Safely get data points
    const safeDataPoints = useMemo(() => {
        if (!dataPoints.length) return null;

        const index = (isQuickAnalysis ? activePosition : currentFrame) ?? 0;

        const { frame, ...rest } = dataPoints[index] ?? dataPoints[dataPoints.length - 1];
        return rest;
    }, [isQuickAnalysis, activePosition, currentFrame, dataPoints]);

    const t = useTranslate();

    if (!Array.isArray(dataPoints) || !dataPoints.length) {
        return (
            <div className={css.root}>
                <div className={css.fallback}>
                    <span
                        className={typography({
                            variant: 'h3',
                        })}
                    >
                        {t('error_message.no_data_available')}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className={css.scrollContainer}>
            <div className={css.root} ref={ref}>
                <ResponsiveContainer minHeight={400}>
                    <ComposedChart
                        data={dataPoints}
                        height={400}
                        defaultShowTooltip={false}
                        margin={{ top: 24, bottom: 40, left: 24, right: 24 }}
                    >
                        <YAxis
                            key={displaySymbol}
                            yAxisId={displaySymbol}
                            orientation="left"
                            interval="equidistantPreserveStart"
                            width={28}
                            dx={-10 / 2}
                            stroke={colors.bluegray[400]}
                            fontSize={10}
                            fontWeight={500}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(data) => `${data}${displaySymbol}`}
                        />

                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        {renderXAxis({
                            isQuickAnalysis,
                            segmentation,
                            axisKey: displaySymbol ?? 'm',
                        })}
                        <ReferenceLine yAxisId={displaySymbol} y={0} stroke={colors.bluegray[400]} />
                        {renderCorridors({ corridorDataPoints, displaySymbol, colorMap, filteredDataKeys })}
                        {renderLines({
                            lines,
                            filteredDataKeys,
                            shouldHideComparison: !showComparison,
                            showComparisonData,
                            apex,
                            transition,
                            displaySymbol,
                            colorMap,
                            translate: t,
                        })}

                        {!deferExpensiveOperations && (
                            <ReferenceLine
                                yAxisId={displaySymbol}
                                x={currentFrame}
                                stroke={colors.blue[500]}
                                strokeWidth={2}
                            />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
                <Legend
                    data={safeDataPoints}
                    lines={lines}
                    colorMap={colorMap}
                    showComparison={showComparison}
                    showComparisonData={showComparisonData}
                    filteredDataKeys={filteredDataKeys}
                    onClickLegend={onClickLegend}
                    guideParameters={guideParameters}
                    onClickGuide={onClickGuide}
                />
            </div>
        </div>
    );
}
